import currencyLib from "currency.js";
import { useRouter } from "next/router";
import { useAppSelector } from "../../../../services/hooks/useReduxHooks";
import {
  selectCartItems,
  selectIsEligibleOffer,
} from "../../../../services/redux/selectors/cartSelectors";
import { selectRedeemedLoyaltyOffer } from "../../../../services/redux/selectors/loyaltyOfferSelectors";
import { useTranslationI18 } from "../../../../services/hooks/useTranslationI18";
import {
  GraphicTypeEnum,
  LocalizedString,
} from "../../../../services/redux/types/CartLoyaltyV2Types";
import { isCartItemCanceled } from "../../../../utils/cart/cartItemDisplay";
import {
  asLegacyOffer,
  isLegacyBogoOrBundleRedeemed,
  legacyOfferId,
} from "../../../../utils/cart/legacyOfferDisplay";
import Offer from "../../../../utils/OfferClass/Offer/Offer";
import {
  ChangeCounterFrom,
  DISPLAYED_NAME,
  MAIN_OFFER_TYPE,
  PagesEnum,
} from "../../../../utils/enums";
import {
  getDisplayedName,
  localizedNumber,
} from "../../../../utils/help/localization/localization";
import { getPageLink } from "../../../../utils/help/navigation/navigation";
import { Counter } from "../../../global/Counter/CounterComponent";
import { MapGeneralOfferIcon } from "../../../global/GeneralOfferIcon/GeneralOfferIcon";
import { MileStone, OfferIcon } from "../../../global/Icons/loyalty.icons";
import { PLink } from "../../../global/PLink";
import { PImage } from "../../../shared/PImage/PImage";
import { DeleteRedIcon, EditIcon } from "../Cart.icons";
import { AddonGroupsComponent } from "./AddonGroup.component";
import styles from "./ItemList.module.scss";
import { removeSpaces } from "./ItemListComponentHelper";
import { BundleItemCard } from "./bundleItem/bundleItemComponent";
import { useCartItemList } from "./useCartItemList";

export const ItemList = () => {
  const router = useRouter();

  const {
    isMobile,
    renderWebViewItem,
    currency,
    isLanguageArabic,
    disableDelete,
    setDisableDelete,
    isInvertedItemCart,
    redeemedOffer,
    isPointingSystemOffer,
    hidePrice,
    deleteItem,
  } = useCartItemList();

  const cartAndLoyaltyReducer = useAppSelector(
    (state) => state.cartAndLoyaltyReducer
  );
  const cartItems = useAppSelector(selectCartItems);
  const isEligibleOffer = useAppSelector(selectIsEligibleOffer);
  const redeemedLoyaltyOffer = useAppSelector(selectRedeemedLoyaltyOffer);

  const { t } = useTranslationI18();

  const editOrderText = t("cart:order-edit");
  const deleteOrderText = t("cart:cart-delete");
  const pointsText = t("pointing-system:points");
  const legacyOffer = asLegacyOffer(redeemedOffer);

  return (
    <div className={styles.card_container} data-testid="itemList">
      {cartItems.map((item, index) => {
        const isOfferItem =
          item.isFreeItem || item.discountedItemPercentage || item.isOfferItem;
        const isBogoOrBundleItem =
          item.isBundleItem ||
          item.isBuyItem ||
          (legacyOffer?.offerType === MAIN_OFFER_TYPE.BOGO && isOfferItem);

        return (
          !isCartItemCanceled(item) &&
          !isBogoOrBundleItem && (
            <div
              className={`${
                !isEligibleOffer && isOfferItem ? styles.dimmed_card : ""
              } ${styles.card}  ${
                isPointingSystemOffer &&
                isOfferItem &&
                styles.pointing_background_color
              } bg__zero ${isInvertedItemCart && "invert"}`}
              key={`${removeSpaces(item.subItemDetails.name)},${index}`}
            >
              <div
                className={`${styles.card_details} ${
                  isOfferItem && styles.free_item
                }`}
              >
                {item.excludeFromOffers &&
                  cartAndLoyaltyReducer.offersArray.length > 0 && (
                    <div
                      data-testid="offers-dont-apply"
                      className={styles.exclude_from_offers}
                    >
                      {t("common:offers-dont-apply")}
                    </div>
                  )}
                <div className={styles.img_container}>
                  {isOfferItem && (
                    <div className={styles.offer_label}>
                      {isPointingSystemOffer ? (
                        <div
                          className={styles.offer_label__loyalty}
                          data-testid={`pointing-system-icon`}
                        >
                          <MileStone />
                        </div>
                      ) : (
                        <>
                          <div className={styles.offer_label_background}>
                            <OfferIcon />
                          </div>
                          <div className={styles.offer_svg}>
                            <MapGeneralOfferIcon
                              graphic={{
                                type: GraphicTypeEnum.ICON,
                                // Loyalty v2: use the offer's own icon (e.g. "percentage",
                                // "money", "gift") from uiConfig; fall back to "free".
                                value: [
                                  redeemedLoyaltyOffer?.uiConfig?.icon?.value ??
                                    "free",
                                ],
                              }}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  <PImage
                    src={item.itemDetails.img_url}
                    alt={item.itemDetails.item_name}
                    fill
                    style={{ objectFit: "cover", objectPosition: "center" }}
                    className={`${styles.img} ${
                      isPointingSystemOffer &&
                      isOfferItem &&
                      styles.pointing_border_color
                    }`}
                  />
                </div>
                <div className={styles.card_content}>
                  <div className={styles.card_body}>
                    <div
                      className={styles.name}
                      data-testid={`cart-item-${item.subItemDetails.name}`}
                    >
                      <span>
                        {(isOfferItem || item.isOfferItem) && (
                          <span>{`${localizedNumber(item.count)}x `}</span>
                        )}

                        {getDisplayedName(
                          item.subItemDetails,
                          DISPLAYED_NAME.FULL_NAME
                        ) ||
                          getDisplayedName(
                            item.subItemDetails,
                            DISPLAYED_NAME.NAME
                          )}
                      </span>
                      {!isBogoOrBundleItem && item.isOfferItem && (
                        <span>
                          <p className={styles.free}>
                            {Offer.getLocalizedValue(
                              legacyOffer?.uiDetails?.cartCard.label
                            ) ||
                              Offer.getLocalizedValue(
                                redeemedLoyaltyOffer?.uiConfig?.cartCard
                                  ?.label as LocalizedString
                              )}
                          </p>
                        </span>
                      )}
                    </div>
                    {isOfferItem &&
                      (legacyOffer?.milestonePoints ||
                        redeemedLoyaltyOffer?.milestonePoints) && (
                        <span
                          className={styles.points}
                          data-testid={`cart-item-${item.subItemDetails.name}-points`}
                        >
                          {localizedNumber(
                            legacyOffer?.milestonePoints ??
                              redeemedLoyaltyOffer?.milestonePoints
                          ) +
                            " " +
                            pointsText}
                          <br />
                        </span>
                      )}
                    <AddonGroupsComponent item={item} />
                    {/* Loyalty v2: priceWithoutOffer is set on the item directly by the new
                        calculate endpoint (no redeemedOffer). Covers free items AND partially
                        discounted items — always shows ~~before~~ after. */}
                    {!hidePrice && !redeemedOffer && item.priceWithoutOffer ? (
                      <>
                        <span className={styles.crossed_price}>
                          {isLanguageArabic
                            ? `${localizedNumber(
                                currencyLib(item.priceWithoutOffer).value
                              )} ${currency}`
                            : `${currency}${localizedNumber(
                                currencyLib(item.priceWithoutOffer).value
                              )}`}
                        </span>
                        <span className={styles.crossed_price_original}>
                          {isLanguageArabic
                            ? `${localizedNumber(
                                currencyLib(item.totalPrice).value
                              )} ${currency}`
                            : `${currency}${localizedNumber(
                                currencyLib(item.totalPrice).value
                              )}`}
                        </span>
                      </>
                    ) : (
                      !hidePrice &&
                      !isOfferItem &&
                      !item.isOfferItem && (
                        <p className={styles.price}>
                          {isLanguageArabic
                            ? `${localizedNumber(
                                currencyLib(item.totalPrice).value
                              )} ${currency}`
                            : `${currency}${localizedNumber(
                                currencyLib(item.totalPrice).value
                              )}`}
                        </p>
                      )
                    )}
                    {item.isOfferItem &&
                      (legacyOffer?.uiDetails?.cartCard
                        ?.showPriceWithoutOffer ||
                        redeemedLoyaltyOffer?.uiConfig?.cartCard
                          ?.showPriceBeforeDiscount) && (
                        <>
                          <span className={styles.crossed_price}>
                            {isLanguageArabic
                              ? `${localizedNumber(
                                  currencyLib(
                                    item.priceWithoutOffer || item.totalPrice
                                  ).value
                                )} ${currency}`
                              : `${currency}${localizedNumber(
                                  currencyLib(
                                    item.priceWithoutOffer || item.totalPrice
                                  ).value
                                )}`}
                          </span>
                          <span className={styles.crossed_price_original}>
                            {isLanguageArabic
                              ? `${localizedNumber(
                                  currencyLib(item.totalPrice).value
                                )} ${currency}`
                              : `${currency}${localizedNumber(
                                  currencyLib(item.totalPrice).value
                                )}`}
                          </span>
                        </>
                      )}
                  </div>
                  {!isBogoOrBundleItem && (
                    <div className={styles.item_actions}>
                      {isMobile ? (
                        <PLink
                          link={getPageLink(router, PagesEnum.Item, {
                            as: item.itemDetails.id + "",
                            queryParams: [
                              `edit=${index}${
                                isOfferItem
                                  ? `&offerId=${
                                      legacyOfferId(redeemedOffer) ?? ""
                                    }`
                                  : ""
                              }`,
                            ],
                          })}
                          passHref
                          replace
                        >
                          <div className={styles.edit}>
                            <EditIcon />
                            <p
                              data-testid={`edit-button-${item.subItemDetails.id}`}
                            >
                              {editOrderText}
                            </p>
                          </div>
                        </PLink>
                      ) : (
                        <a
                          onClick={() => {
                            renderWebViewItem(
                              item.itemDetails.id,
                              index,
                              item.isFreeItem,
                              legacyOfferId(redeemedOffer)
                            );
                          }}
                          aria-disabled={disableDelete}
                          style={{
                            pointerEvents: disableDelete ? "none" : "auto",
                            opacity: disableDelete ? 0.5 : 1,
                          }}
                        >
                          <div className={styles.edit}>
                            <EditIcon />
                            <p
                              data-testid={`edit-button-${item.subItemDetails.id}`}
                            >
                              {editOrderText}
                            </p>
                          </div>
                        </a>
                      )}
                      {!isOfferItem && !item.isOfferItem && (
                        <div
                          className={styles.delete}
                          data-testid={`delete-icon-${item.itemDetails.item_name}`}
                        >
                          <DeleteRedIcon />
                          <p
                            onClick={() =>
                              deleteItem(
                                index,
                                "lineId" in item ? item.lineId : undefined
                              )
                            }
                            style={{
                              pointerEvents: disableDelete ? "none" : "auto",
                              opacity: disableDelete ? 0.5 : 1,
                            }}
                          >
                            {deleteOrderText}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {!isBogoOrBundleItem && !isOfferItem && !item.isOfferItem && (
                <div className={styles.counter}>
                  <Counter
                    count={item.count}
                    itemIndex={index}
                    data={cartItems[index]}
                    changeCounterView={ChangeCounterFrom.CartView}
                    setDisableDelete={setDisableDelete}
                  />
                </div>
              )}
            </div>
          )
        );
      })}
      {isLegacyBogoOrBundleRedeemed(redeemedOffer) && <BundleItemCard />}
    </div>
  );
};
